import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Typography } from '@mui/material';
import { Button } from 'antd';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { PastSurveyData } from '~/common/types/api/users';

const ContentTheme = styled.div`
  background: ${PALETTE.blue.main};
  width: 940px;
  height: 120px;
  border-bottom: 1px solid ${PALETTE.border.frame};
  padding: 40px;
  margin-bottom: 17px;
`;
const ContentBox = styled.div`
  width: 100%;
  padding: 10px;
  border: 1px solid ${PALETTE.border.frame};
  border-top: none;
  background: ${PALETTE.white};
`;
const Title = styled(Typography)`
  font-size: ${FONT_SIZES.body1};
  color: ${PALETTE.text.darkBlue};
  font-weight: bold;
`;
const TitleResult = styled(Typography)`
  font-size: ${FONT_SIZES.tableBody1};
  color: ${PALETTE.text.main};
  font-weight: bold;
`;
const ContentNumberQuestion = styled(Typography)`
  font-size: ${FONT_SIZES.title3};
  color: ${PALETTE.text.main};
  font-weight: 500;
`;
const NumberQuestionStyle = styled(Typography)`
  font-size: ${FONT_SIZES.title1};
  color: ${PALETTE.text.main};
  margin: 0px 19px;
  font-weight: 500;
`;
const ContentSurvey = styled(Typography)`
  width: 125px;
  font-size: ${FONT_SIZES.title5};
  margin-left: 5px;
  color: ${PALETTE.text.sub};
`;
const ContentDescription = styled.div`
  display: flex;
  margin-top: 10px;
  align-items: baseline;
`;
const ContentResult = styled.div`
  align-items: center;
`;
const ContentResultStyle = styled.div`
  width: 700px;
`;
const ResultStyle = styled.div`
  align-items: center;
  margin: 20px 19px;
  border-bottom: 1px solid ${PALETTE.border.frame};
  padding: 0px 23px 23px 23px;
  display: inline-flex;
`;
const ButtonStyle = styled.div`
  display: grid;
  margin-left: 45px;
`;
const ButtonPreview = styled(Button)`
  width: 116px;
  height: 28px;
  margin-bottom: 12px;
  color: ${PALETTE.text.sub};
  font-size: ${FONT_SIZES.note1};
  border: 1px solid ${PALETTE.border.bluePale};
  display: flex;
`;
const ButtonCreate = styled(Button)`
  width: 116px;
  height: 28px;
  color: ${PALETTE.white};
  font-size: ${FONT_SIZES.note1};
  background: ${PALETTE.primary.main};
  &:hover {
    opacity: 0.6;
    color: ${PALETTE.white} !important;
  }
`;

const VisibilityRoundedIconStyle = styled(VisibilityRoundedIcon)`
  font-size: 16px;
  margin: 1px 4px 0px 0px;
  color: ${PALETTE.text.sub};
`;

type Props = {
  data: PastSurveyData[];
  showPreviewPage: (miniEnqueteId: number) => void;
  buttonCreate: (miniEnqueteId: number) => void;
};

const PastSurveyTab: React.FC<Props> = ({
  data,
  showPreviewPage,
  buttonCreate,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  return (
    <ContentBox>
      <ContentTheme>
        <Title>{t('registration.pastSurvey.pastQuestionTitle')}</Title>
      </ContentTheme>
      <ContentResult>
        {data.map((item) => {
          const createdDate = new Date(item.survey.created);
          const modifiedDate = new Date(item.survey.modified);
          const formattedCreatedDate = createdDate
            .toLocaleDateString('zh-Hans-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })
            .split('/')
            .join('/');
          const formattedModifiedDate = modifiedDate
            .toLocaleDateString('zh-Hans-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })
            .split('/')
            .join('/');

          return (
            <div key={`'category_'${item.miniEnqueteId}`}>
              <ResultStyle key={`'pastSurvey_'${item.survey.id}`}>
                <ContentResultStyle>
                  <TitleResult>{item.survey.name}</TitleResult>
                  <ContentDescription>
                    <ContentNumberQuestion>設問数</ContentNumberQuestion>
                    <NumberQuestionStyle>{item.survey.numberOfQuestions}</NumberQuestionStyle>
                    <ContentSurvey>{`アンケートID: ${item.survey.id}`}</ContentSurvey>
                    <ContentSurvey>{`作成日: ${formattedCreatedDate}`}</ContentSurvey>
                    <ContentSurvey>{`更新日: ${formattedModifiedDate}`}</ContentSurvey>
                  </ContentDescription>
                </ContentResultStyle>
                <ButtonStyle>
                  <ButtonPreview
                    data-testid={`button_preview_past_survey_${item.survey.id}`}
                    onClick={() => showPreviewPage(item.survey.id)}
                  >
                    <VisibilityRoundedIconStyle />
                    {t('registration.pastSurvey.previewButton')}
                  </ButtonPreview>
                  <ButtonCreate
                    data-testid={`button_create_past_survey_${item.survey.id}`}
                    onClick={() => buttonCreate(item.miniEnqueteId)}
                  >
                    {t('registration.pastSurvey.createButton')}
                  </ButtonCreate>
                </ButtonStyle>
              </ResultStyle>
            </div>
          );
        })}
      </ContentResult>
    </ContentBox>
  );
};

export default PastSurveyTab;
